import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import React, { Component } from 'react';
import Data from '../data';
import ModalModule from '../modal';
import UI from '../ui';
import Utils from '../utils';
import { TotalChargeSummary } from './TotalChargeSummary.component';

const { ModalContext } = ModalModule;
const {
	WrappedButton,
	WrappedButtonType,
	WrappedCheckbox,
	InlineMessage,
	WrappedInputNumberWithLabel,
} = UI;
const { Communication, Session, Util, Navigation } = Utils;
const { InternalCapability } = Data;

class AutoFundAccount extends Component {
	constructor() {
		super();

		this.state = {
			isAutoPurchaseEnabled: false,
			autoPurchaseAmount: 0,
			autoPurchaseThreshold: 0,
			errorMessage: '',
			showSuccessMessage: false,
			isRedirecting: false,
			isSurchargesMessageDismissed: true,
			isNsaSurchargeEnrolled: false,
			nsaSurchargeRate: 0,
			previewCalculations: {
				totalAmount: 0,
				purchaseAmount: 0,
				surchargeAmount: 0,
			},
			isLoadingPreview: false,
		};

		this.customAmountDebounceTimer = null;
	}

	navigateToPaymentSettings = () => {
		this.setState({ isRedirecting: true });
		Navigation.navigateTo('paymentSettings');
	};

	componentDidMount() {
		this.getAutoPurchaseSettings();

		const { DismissNsaSurchargesMessageInAutoFunds } = Session.getPreferences();
		const { isNsaSurchargeEnrolled, nsaSurchargePercentage } =
			Session.getDynamicSettings();

		this.setState({
			isSurchargesMessageDismissed:
				DismissNsaSurchargesMessageInAutoFunds === '1',
			isNsaSurchargeEnrolled,
			nsaSurchargeRate: nsaSurchargePercentage,
		});
	}

	componentWillUnmount() {
		if (this.customAmountDebounceTimer) {
			clearTimeout(this.customAmountDebounceTimer);
		}
	}

	async componentDidUpdate(_, prevState) {
		const { autoPurchaseAmount, isNsaSurchargeEnrolled } = this.state;

		if (
			autoPurchaseAmount > 0 &&
			prevState.autoPurchaseAmount !== autoPurchaseAmount &&
			isNsaSurchargeEnrolled
		) {
			this.setState({ isLoadingPreview: true });

			const { data } = await Communication.previewAddFunds({
				PurchaseAmount: autoPurchaseAmount,
			});

			this.setState({
				previewCalculations: {
					totalAmount: data?.Amount?.Amount,
					purchaseAmount: data?.BaseAmount?.Amount,
					surchargeAmount: data?.TotalTransactionFee?.Amount,
				},
				isLoadingPreview: false,
			});
		}
	}

	getAutoPurchaseSettings = async () => {
		const { showError } = this.context;

		const response = await Communication.getAutoPurchaseSettings();

		if (Communication.isSuccessful(response)) {
			const {
				isAutoPurchaseEnabled,
				autoPurchaseAmount,
				autoPurchaseThreshold,
			} = isObject(response.data) ? Util.toCamelCase(response.data) : {};

			this.setState({
				...(!this.state.isAutoPurchaseEnabled && {
					isAutoPurchaseEnabled,
				}),
				autoPurchaseAmount: isNumber(autoPurchaseAmount)
					? autoPurchaseAmount
					: 0,
				autoPurchaseThreshold: isNumber(autoPurchaseThreshold)
					? autoPurchaseThreshold
					: 0,
			});
		} else {
			showError({ response });
		}
	};

	dismissNsaSurchargesMessage = () => {
		Session.savePreferences([
			{
				Name: 'DismissNsaSurchargesMessageInAutoFunds',
				Value: '1',
			},
		]);
		this.setState({ isSurchargesMessageDismissed: true });
	};

	setAutoPurchaseSettings = async () => {
		const { showError } = this.context;
		const { closeAutoFundAccountModal, completionCallback } = this.props;
		const { isAutoPurchaseEnabled, autoPurchaseAmount, autoPurchaseThreshold } =
			this.state;

		const response = await Communication.setAutoPurchaseSettings({
			is_auto_purchase_enabled: isAutoPurchaseEnabled,
			auto_purchase_amount: isAutoPurchaseEnabled ? autoPurchaseAmount : 0.0,
			auto_purchase_threshold: isAutoPurchaseEnabled
				? autoPurchaseThreshold
				: 0.0,
			currency: 'usd',
		});

		if (Communication.isSuccessful(response)) {
			this.getAutoPurchaseSettings();

			// refresh account info to update auto-fund data in legacy repo
			if (completionCallback) {
				completionCallback({ type: 'AutoFundAccount' });
			}

			// the method is available if this component is embedded in AutoFundAccountModal
			if (closeAutoFundAccountModal) {
				closeAutoFundAccountModal();
			} else {
				this.setState({
					showSuccessMessage: true,
				});
			}
		} else {
			showError({ response });
		}
	};

	onClickCheckbox = () => {
		this.setState(({ isAutoPurchaseEnabled }) => ({
			isAutoPurchaseEnabled: !isAutoPurchaseEnabled,
		}));
	};

	onChangeInput = ({ target: { name, value } }) => {
		if (this.customAmountDebounceTimer) {
			clearTimeout(this.customAmountDebounceTimer);
		}

		this.customAmountDebounceTimer = setTimeout(() => {
			this.setState({
				[name]: Number(value),
			});
		}, 2000);
	};

	onClickSave = () => {
		const { maxBalanceAmountAllowed } = Session.getBalance();
		const maxPostageBalance = isNumber(maxBalanceAmountAllowed)
			? maxBalanceAmountAllowed
			: 0;
		const { isAutoPurchaseEnabled, autoPurchaseAmount } = this.state;
		let errorMessage = '';

		if (!Session.hasCapability(InternalCapability.ChangePaymentMethod)) {
			errorMessage =
				'Your account is not authorized to purchase postage. Please contact your account administrator.';
		} else if (isAutoPurchaseEnabled && autoPurchaseAmount < 100) {
			errorMessage = 'The minimum automatic purchase amount is $100.';
		} else if (
			isAutoPurchaseEnabled &&
			autoPurchaseAmount > maxPostageBalance
		) {
			errorMessage = `Your maximum postage balance is $${maxPostageBalance}. Postage purchases cannot exceed this amount.`;
		}

		this.setState(
			{
				errorMessage,
			},
			() => {
				// make a call to save auto-buy settings if no errors
				if (errorMessage === '') {
					this.setAutoPurchaseSettings();
				}
			}
		);
	};

	render() {
		const {
			isAutoPurchaseEnabled,
			errorMessage,
			autoPurchaseAmount,
			showSuccessMessage,
			autoPurchaseThreshold,
			isNsaSurchargeEnrolled,
			isRedirecting,
			isSurchargesMessageDismissed,
			nsaSurchargeRate,
			previewCalculations,
		} = this.state;

		const shouldShowTotalChargeSummary =
			isNsaSurchargeEnrolled && nsaSurchargeRate > 0;

		const shouldShowSurchargesMessage =
			isNsaSurchargeEnrolled && !isSurchargesMessageDismissed;

		return (
			<div className="common-payment auto-fund-account p-grid p-dir-col">
				{showSuccessMessage ? (
					<>
						<div className="p-col-12">
							Auto-Fund Settings have been successfully updated.
						</div>
						<div className="p-col-12">
							Please close this window to continue.
						</div>
					</>
				) : (
					<>
						{shouldShowSurchargesMessage && (
							<InlineMessage
								type="information"
								header={`A ${nsaSurchargeRate}% fee applies when adding funds to your balance, including auto-funding, using a credit card.`}
								text="Switch to Pay by Bank to avoid the fee."
								canClose={true}
								onClose={this.dismissNsaSurchargesMessage}
								dataqa="surcharge-message-add-funds"
								actions={
									<WrappedButton
										label={isRedirecting ? 'Redirecting...' : 'Go to settings'}
										buttonType={WrappedButtonType.TertiaryWhite}
										dataqa="surcharge-info-go-to-settings-button"
										onClick={this.navigateToPaymentSettings}
										disabled={isRedirecting}
									/>
								}
							/>
						)}

						<div className="auto-purchase-row">
							<WrappedCheckbox
								inputId="settings-autobuy"
								htmlFor="settings-autobuy"
								name="isAutoPurchaseEnabled"
								checked={isAutoPurchaseEnabled}
								onChange={this.onClickCheckbox}
								dataqa="settings-autobuyenabled"
							>
								Automatically purchase
							</WrappedCheckbox>
							<WrappedInputNumberWithLabel
								uiElementConfig={{
									className: `${isAutoPurchaseEnabled ? '' : 'disabled'}`,
									prefix: '$ ',
									value: autoPurchaseAmount,
									name: 'autoPurchaseAmount',
									placeholder: '$ 0.00',
									min: 0,
									mode: 'decimal',
									minFractionDigits: 2,
									maxFractionDigits: 2,
									incrementButtonIcon: 'pi pi-chevron-up',
									decrementButtonIcon: 'pi pi-chevron-down',
									showButtons: true,
									onChange: this.onChangeInput,
									keyfilter: 'pint',
									disabled: !isAutoPurchaseEnabled,
									dataqa: 'settings-purchaseamount',
								}}
							/>
						</div>
						<div>
							<WrappedInputNumberWithLabel
								className="normal-weight-label"
								label="If postage balance is below"
								uiElementConfig={{
									className: `${isAutoPurchaseEnabled ? '' : 'disabled'}`,
									prefix: '$ ',
									value: autoPurchaseThreshold,
									name: 'autoPurchaseThreshold',
									placeholder: '$ 0.00',
									min: 0,
									mode: 'decimal',
									minFractionDigits: 2,
									maxFractionDigits: 2,
									incrementButtonIcon: 'pi pi-chevron-up',
									decrementButtonIcon: 'pi pi-chevron-down',
									showButtons: true,
									onChange: this.onChangeInput,
									keyfilter: 'pint',
									disabled: !isAutoPurchaseEnabled,
									dataqa: 'settings-triggeramount',
								}}
							/>
						</div>

						{shouldShowTotalChargeSummary && (
							<TotalChargeSummary
								className="auto-fund-total-charge-summary"
								purchaseAmount={previewCalculations.purchaseAmount}
								surchargeAmount={previewCalculations.surchargeAmount}
								totalAmount={previewCalculations.totalAmount}
								isLoading={this.state.isLoadingPreview}
							/>
						)}

						{errorMessage && (
							<div className="p-col-12 error-area">
								<p className="error-icon">{errorMessage}</p>
							</div>
						)}
						<div className="auto-fund-save-button-container">
							<WrappedButton
								label="Save"
								onClick={this.onClickSave}
								dataqa="settings-auto-fund-save"
							/>
						</div>
					</>
				)}
			</div>
		);
	}
}

AutoFundAccount.contextType = ModalContext;

export default AutoFundAccount;
